/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  isSameDay,
  isDateDisabled,
  formatDateDisplay,
  convertTo24Hour,
} from "../../lib/date-utils";
import type { TimeValue } from "../../lib/date-utils";
import { cn } from "../../lib/utils";
import { Select } from "./Select";

interface DateTimePickerProps {
  dateValue: Date | null;
  timeValue?: TimeValue | null;
  onDateChange: (date: Date | null) => void;
  onTimeChange?: (time: TimeValue | null) => void;
  label?: string;
  datePlaceholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  minTime?: TimeValue;
  timeIncrement?: number;
  variant?: "datetime" | "date" | "month";
  error?: string;
  editOnly?: boolean;
  disabled?: boolean;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const DateTimePicker = ({
  dateValue,
  timeValue,
  onDateChange,
  onTimeChange,
  label,
  datePlaceholder = "Select date",
  className = "",
  minDate,
  maxDate,
  minTime,
  timeIncrement = 30,
  variant = "datetime",
  error,
  editOnly = false,
  disabled = false,
}: DateTimePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"date" | "time">("date");
  const [isMobile, setIsMobile] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [datePosition, setDatePosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({
    top: 0,
    left: 0,
    width: 300,
    maxHeight: 420,
  });
  const [timePosition, setTimePosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({
    top: 0,
    left: 0,
    width: 200,
    maxHeight: 420,
  });

  const hasError = !!error;

  const [viewDate, setViewDate] = useState(() => dateValue || new Date());
  const [showYearPicker, setShowYearPicker] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);
  const periodInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const hours = timeValue?.hours ?? 12;
  const minutes = timeValue?.minutes ?? 0;
  const period = timeValue?.period ?? "PM";

  const [dateInputValue, setDateInputValue] = useState("");

  useEffect(() => {
    if (dateValue && editOnly) {
      const month = (dateValue.getMonth() + 1).toString().padStart(2, "0");
      const day = dateValue.getDate().toString().padStart(2, "0");
      const year = dateValue.getFullYear();
      setDateInputValue(`${month}/${day}/${year}`);
    } else if (!dateValue && editOnly) {
      setDateInputValue("");
    }
  }, [dateValue, editOnly]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (dateValue) {
      setViewDate(dateValue);
    }
  }, [dateValue]);

  const updatePosition = useCallback(() => {
    if (containerRef.current && isOpen && !isMobile) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const VERTICAL_GAP = 4;
        const VIEWPORT_PADDING = 16;
        const IDEAL_HEIGHT = 330;

        const spaceBelow = viewportHeight - rect.bottom - VERTICAL_GAP;
        const spaceAbove = rect.top - VERTICAL_GAP;

        const shouldShowAbove =
          spaceBelow < IDEAL_HEIGHT && spaceAbove > spaceBelow;

        const maxHeight = shouldShowAbove
          ? Math.min(IDEAL_HEIGHT, spaceAbove - VIEWPORT_PADDING)
          : Math.min(IDEAL_HEIGHT, spaceBelow - VIEWPORT_PADDING);

        if (activeTab === "date") {
          const DATE_DROPDOWN_WIDTH = 300;
          let left = rect.left;
          if (left + DATE_DROPDOWN_WIDTH > viewportWidth - VIEWPORT_PADDING) {
            left = viewportWidth - DATE_DROPDOWN_WIDTH - VIEWPORT_PADDING;
          }
          if (left < VIEWPORT_PADDING) {
            left = VIEWPORT_PADDING;
          }

          if (shouldShowAbove) {
            setDatePosition({
              bottom: viewportHeight - rect.top + VERTICAL_GAP,
              left,
              width: DATE_DROPDOWN_WIDTH,
              maxHeight,
            });
          } else {
            setDatePosition({
              top: rect.bottom + VERTICAL_GAP,
              left,
              width: DATE_DROPDOWN_WIDTH,
              maxHeight,
            });
          }
        } else {
          const TIME_DROPDOWN_WIDTH = 120;
          let left = rect.right - TIME_DROPDOWN_WIDTH;
          if (left + TIME_DROPDOWN_WIDTH > viewportWidth - VIEWPORT_PADDING) {
            left = viewportWidth - TIME_DROPDOWN_WIDTH - VIEWPORT_PADDING;
          }
          if (left < VIEWPORT_PADDING) {
            left = VIEWPORT_PADDING;
          }

          if (shouldShowAbove) {
            setTimePosition({
              bottom: viewportHeight - rect.top + VERTICAL_GAP,
              left,
              width: TIME_DROPDOWN_WIDTH,
              maxHeight,
            });
          } else {
            setTimePosition({
              top: rect.bottom + VERTICAL_GAP,
              left,
              width: TIME_DROPDOWN_WIDTH,
              maxHeight,
            });
          }
        }
      });
    }
  }, [isOpen, isMobile, activeTab]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      updatePosition();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, isMobile, updatePosition]);

  useEffect(() => {
    if (!isOpen || isMobile) return;

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, isMobile, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isMobile]);

  const daysInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, [viewDate]);

  const firstDayOfMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    return new Date(year, month, 1).getDay();
  }, [viewDate]);

  const today = useMemo(() => new Date(), []);

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), i));
    }
    return days;
  }, [viewDate, daysInMonth, firstDayOfMonth]);

  const yearRange = useMemo(() => {
    const currentYear = viewDate.getFullYear();
    const minYear = minDate?.getFullYear() || currentYear - 100;
    const maxYear = maxDate?.getFullYear() || currentYear + 10;
    const years: number[] = [];
    for (let y = minYear; y <= maxYear; y++) {
      years.push(y);
    }
    return years;
  }, [viewDate, minDate, maxDate]);

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDateSelect = useCallback(
    (date: Date) => {
      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0, 0);
      onDateChange(selectedDate);
      setIsOpen(false);
    },
    [onDateChange]
  );

  const handleHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onTimeChange) return;
      const input = e.target.value;

      if (input === "") return;

      const val = parseInt(input);
      if (isNaN(val)) return;

      if (val >= 1 && val <= 12) {
        const currentHour = hours;
        let newHour = val;

        if (currentHour >= 1 && currentHour <= 9 && input.length === 1) {
          newHour = currentHour * 10 + val;
          if (newHour > 12) newHour = val;
        }

        onTimeChange({ hours: newHour, minutes, period });

        setTimeout(() => {
          if (newHour >= 10 || (input.length === 1 && val > 1)) {
            minutesInputRef.current?.focus();
            minutesInputRef.current?.select();
          } else {
            hoursInputRef.current?.select();
          }
        }, 0);
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handleHoursBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleMinutesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onTimeChange) return;
      const input = e.target.value;

      if (input === "") return;

      const val = parseInt(input);
      if (isNaN(val)) return;

      if (val >= 0 && val <= 59) {
        const currentMinutes = minutes;
        let newMinutes = val;

        if (currentMinutes >= 0 && currentMinutes <= 5 && input.length === 1) {
          newMinutes = currentMinutes * 10 + val;
          if (newMinutes > 59) newMinutes = val;
        }

        onTimeChange({ hours, minutes: newMinutes, period });

        setTimeout(() => {
          if (newMinutes >= 10 || (input.length === 1 && val > 5)) {
            periodInputRef.current?.focus();
            periodInputRef.current?.select();
          } else {
            minutesInputRef.current?.select();
          }
        }, 0);
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handleMinutesBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onTimeChange) return;
      const input = e.target.value.toUpperCase();
      const lastChar = input.slice(-1);

      if (lastChar === "A" && period !== "AM") {
        onTimeChange({ hours, minutes, period: "AM" });
        setTimeout(() => periodInputRef.current?.select(), 0);
      } else if (lastChar === "P" && period !== "PM") {
        onTimeChange({ hours, minutes, period: "PM" });
        setTimeout(() => periodInputRef.current?.select(), 0);
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handlePeriodBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleHoursKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        minutesInputRef.current?.focus();
        minutesInputRef.current?.select();
      } else if (e.key === "ArrowUp") {
        if (!onTimeChange) return;
        e.preventDefault();
        const newHour = hours === 12 ? 1 : hours + 1;
        onTimeChange({ hours: newHour, minutes, period });
        setTimeout(() => hoursInputRef.current?.select(), 0);
      } else if (e.key === "ArrowDown") {
        if (!onTimeChange) return;
        e.preventDefault();
        const newHour = hours === 1 ? 12 : hours - 1;
        onTimeChange({ hours: newHour, minutes, period });
        setTimeout(() => hoursInputRef.current?.select(), 0);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        minutesInputRef.current?.focus();
        minutesInputRef.current?.select();
      } else if (e.key.toLowerCase() === "a" || e.key.toLowerCase() === "p") {
        e.preventDefault();
        periodInputRef.current?.focus();
        periodInputRef.current?.select();
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handleMinutesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab") {
        if (e.shiftKey) {
          e.preventDefault();
          hoursInputRef.current?.focus();
          hoursInputRef.current?.select();
        } else {
          e.preventDefault();
          periodInputRef.current?.focus();
          periodInputRef.current?.select();
        }
      } else if (e.key === "ArrowUp") {
        if (!onTimeChange) return;
        e.preventDefault();
        const newMinutes = minutes === 59 ? 0 : minutes + 1;
        onTimeChange({ hours, minutes: newMinutes, period });
        setTimeout(() => minutesInputRef.current?.select(), 0);
      } else if (e.key === "ArrowDown") {
        if (!onTimeChange) return;
        e.preventDefault();
        const newMinutes = minutes === 0 ? 59 : minutes - 1;
        onTimeChange({ hours, minutes: newMinutes, period });
        setTimeout(() => minutesInputRef.current?.select(), 0);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        hoursInputRef.current?.focus();
        hoursInputRef.current?.select();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        periodInputRef.current?.focus();
        periodInputRef.current?.select();
      } else if (e.key.toLowerCase() === "a" || e.key.toLowerCase() === "p") {
        e.preventDefault();
        periodInputRef.current?.focus();
        periodInputRef.current?.select();
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handlePeriodKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        minutesInputRef.current?.focus();
        minutesInputRef.current?.select();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        if (!onTimeChange) return;
        e.preventDefault();
        const newPeriod = period === "AM" ? "PM" : "AM";
        onTimeChange({ hours, minutes, period: newPeriod });
        setTimeout(() => periodInputRef.current?.select(), 0);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        minutesInputRef.current?.focus();
        minutesInputRef.current?.select();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
      }
    },
    [onTimeChange, hours, minutes, period]
  );

  const handleDateInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];
      const isNumber = /^[0-9]$/.test(e.key);
      const isSlash = e.key === "/";

      if (!allowedKeys.includes(e.key) && !isNumber && !isSlash) {
        e.preventDefault();
        return;
      }

      if (e.key === "Backspace") {
        const input = e.currentTarget;
        const cursorPos = input.selectionStart || 0;
        const value = input.value;

        if (cursorPos > 0 && value[cursorPos - 1] === "/") {
          e.preventDefault();
          const newValue =
            value.slice(0, cursorPos - 2) + value.slice(cursorPos);
          setDateInputValue(newValue);
          setTimeout(() => {
            input.setSelectionRange(cursorPos - 2, cursorPos - 2);
          }, 0);
        }
      }
    },
    []
  );

  const handleDateInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      value = value.replace(/[^0-9/]/g, "");

      const parts = value.split("/");
      if (parts.length > 3) {
        value = parts.slice(0, 3).join("/");
      }

      if (parts[0] && parts[0].length > 2) {
        parts[0] = parts[0].slice(0, 2);
      }
      if (parts[1] && parts[1].length > 2) {
        parts[1] = parts[1].slice(0, 2);
      }
      if (parts[2] && parts[2].length > 4) {
        parts[2] = parts[2].slice(0, 4);
      }

      value = parts.join("/");

      const digitsOnly = value.replace(/\//g, "");

      if (digitsOnly.length >= 2 && !value.includes("/")) {
        const mm = digitsOnly.slice(0, 2).padStart(2, "0");
        value = mm + "/" + digitsOnly.slice(2);
      } else if (digitsOnly.length >= 4 && value.split("/").length === 2) {
        const [mm = "", rest = ""] = value.split("/");
        const paddedMm = mm.padStart(2, "0");
        const dd = rest.slice(0, 2).padStart(2, "0");
        value = paddedMm + "/" + dd + "/" + rest.slice(2);
      }

      if (parts.length === 2 && parts[1] === "" && value.endsWith("/")) {
        parts[0] = (parts[0] ?? "").padStart(2, "0");
        value = parts[0] + "/";
      }

      setDateInputValue(value);

      const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        const month = parseInt(match[1] ?? "", 10);
        const day = parseInt(match[2] ?? "", 10);
        const year = parseInt(match[3] ?? "", 10);

        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          const date = new Date(year, month - 1, day);
          if (
            date.getMonth() === month - 1 &&
            date.getDate() === day &&
            date.getFullYear() === year
          ) {
            onDateChange(date);
            setViewDate(date);
          }
        }
      }
    },
    [onDateChange]
  );

  const handleDateInputBlur = useCallback(() => {
    setIsFocused(false);

    let value = dateInputValue;
    const parts = value.split("/");

    if (parts.length >= 1 && parts[0]) {
      parts[0] = parts[0].padStart(2, "0");
    }
    if (parts.length >= 2 && parts[1]) {
      parts[1] = parts[1].padStart(2, "0");
    }

    const formatted = parts.join("/");

    const match = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const month = parseInt(match[1] ?? "", 10);
      const day = parseInt(match[2] ?? "", 10);
      const year = parseInt(match[3] ?? "", 10);

      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const date = new Date(year, month - 1, day);
        if (
          date.getMonth() === month - 1 &&
          date.getDate() === day &&
          date.getFullYear() === year
        ) {
          if (formatted !== value) {
            setDateInputValue(formatted);
          }
          onDateChange(date);
          setViewDate(date);
          return;
        }
      }
    }

    if (dateValue) {
      const month = (dateValue.getMonth() + 1).toString().padStart(2, "0");
      const day = dateValue.getDate().toString().padStart(2, "0");
      const year = dateValue.getFullYear();
      setDateInputValue(`${month}/${day}/${year}`);
    } else {
      setDateInputValue("");
    }
  }, [dateInputValue, dateValue, onDateChange]);

  const displayDate = useMemo(() => {
    if (!dateValue) return datePlaceholder;

    if (variant === "month") {
      return dateValue.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }

    return formatDateDisplay(dateValue);
  }, [dateValue, datePlaceholder, variant]);

  const displayTime = useMemo(() => {
    if (variant !== "datetime") return null;
    if (!timeValue) return "Select time";
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
  }, [variant, timeValue, hours, minutes, period]);

  const timeOptions = useMemo(() => {
    const options: Array<TimeValue & { label: string }> = [];

    for (const p of ["AM", "PM"] as const) {
      for (let h = 12; h <= 12; h++) {
        for (let m = 0; m < 60; m += timeIncrement) {
          if (minTime) {
            const hour24 = convertTo24Hour(h, p);
            const minHour24 = convertTo24Hour(minTime.hours, minTime.period);

            if (hour24 < minHour24) continue;
            if (hour24 === minHour24 && m < minTime.minutes) continue;
          }

          const label = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${p}`;
          options.push({ hours: h, minutes: m, period: p, label });
        }
      }

      for (let h = 1; h < 12; h++) {
        for (let m = 0; m < 60; m += timeIncrement) {
          if (minTime) {
            const hour24 = convertTo24Hour(h, p);
            const minHour24 = convertTo24Hour(minTime.hours, minTime.period);

            if (hour24 < minHour24) continue;
            if (hour24 === minHour24 && m < minTime.minutes) continue;
          }

          const label = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${p}`;
          options.push({ hours: h, minutes: m, period: p, label });
        }
      }
    }

    return options;
  }, [minTime, timeIncrement]);

  const handleTimeSelect = useCallback(
    (time: TimeValue) => {
      if (!onTimeChange) return;
      onTimeChange(time);
      setIsOpen(false);
    },
    [onTimeChange]
  );

  const renderCalendar = () => (
    <div className="space-y-3">
      {!showYearPicker ? (
        <>
          <div className="flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-bg-hover rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={() => setShowYearPicker(true)}
              className="flex-1 text-center font-display text-base text-text-primary hover:bg-bg-hover rounded-lg px-2 py-1 transition-colors cursor-pointer"
            >
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-bg-hover rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {DAYS.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-text-tertiary py-1.5"
              >
                {day}
              </div>
            ))}
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="h-8" />;
              }

              const isSelected = isSameDay(date, dateValue);
              const isToday = isSameDay(date, today);
              const isDisabled = isDateDisabled(date, minDate, maxDate);

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => !isDisabled && handleDateSelect(date)}
                  disabled={isDisabled}
                  style={
                    isToday && !isSelected && !isDisabled
                      ? { boxShadow: `inset 0 -2px 0 0 var(--color-accent)` }
                      : undefined
                  }
                  className={cn(
                    "h-8 w-full rounded-lg flex items-center justify-center text-sm transition-all",
                    isDisabled
                      ? "text-text-tertiary opacity-30 cursor-not-allowed"
                      : isSelected
                        ? "bg-interactive text-bg-base font-semibold shadow-card"
                        : isToday
                          ? "font-semibold text-text-primary hover:bg-bg-hover"
                          : "font-medium text-text-primary hover:bg-bg-hover"
                  )}
                  aria-label={date.toLocaleDateString()}
                  aria-pressed={isSelected}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 pb-2 border-b border-border">
            <button
              type="button"
              onClick={() => setShowYearPicker(false)}
              className="p-1.5 hover:bg-bg-hover rounded-lg transition-colors"
              aria-label="Back to calendar"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-display text-sm text-text-primary">
              Select Month & Year
            </span>
            <div className="w-6" />
          </div>
          <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {MONTHS.map((month, index) => (
              <button
                key={month}
                type="button"
                onClick={() => {
                  setViewDate(new Date(viewDate.getFullYear(), index, 1));
                  setShowYearPicker(false);
                }}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  index === viewDate.getMonth()
                    ? "bg-interactive text-bg-base"
                    : "text-text-primary hover:bg-bg-hover"
                )}
              >
                {month.slice(0, 3)}
              </button>
            ))}
          </div>
          <div className="border-t border-border my-3" />
          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {yearRange.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => {
                  setViewDate(new Date(year, viewDate.getMonth(), 1));
                  setShowYearPicker(false);
                }}
                className={cn(
                  "px-2 py-2 rounded-lg text-sm font-medium transition-colors",
                  year === viewDate.getFullYear()
                    ? "bg-interactive text-bg-base"
                    : "text-text-primary hover:bg-bg-hover"
                )}
              >
                {year}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderMobileSheet = () => (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={() => setIsOpen(false)}
      />
      <div className="fixed inset-x-0 bottom-0 z-50 bg-bg-elevated rounded-t-3xl shadow-elevated animate-slide-up">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display text-lg text-text-primary">
            {activeTab === "date" ? "Select Date" : "Select Time"}
          </h3>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-bg-hover rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>
        <div className="p-4 max-h-[65vh] overflow-auto">
          {activeTab === "date" ? (
            renderCalendar()
          ) : (
            <div className="space-y-2">
              {timeOptions.map((option) => {
                const selected =
                  timeValue?.hours === option.hours &&
                  timeValue?.minutes === option.minutes &&
                  timeValue?.period === option.period;
                return (
                  <button
                    key={`${option.hours}-${option.minutes}-${option.period}`}
                    type="button"
                    onClick={() => handleTimeSelect(option)}
                    className={cn(
                      "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors",
                      selected
                        ? "bg-interactive text-bg-base font-semibold"
                        : "text-text-primary hover:bg-bg-hover"
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (variant === "month") {
    const currentYear = new Date().getFullYear();
    const minYear = minDate?.getFullYear() || currentYear - 90;
    const maxYear = maxDate?.getFullYear() || currentYear + 10;

    const selectedYear = dateValue?.getFullYear();
    const selectedMonth = dateValue?.getMonth();

    const minMonth = minDate?.getMonth();
    const maxMonth = maxDate?.getMonth();
    const minYearValue = minDate?.getFullYear();
    const maxYearValue = maxDate?.getFullYear();

    const availableMonths = MONTHS.map((month, index) => {
      let isDisabled = false;

      if (selectedYear !== undefined) {
        if (
          minYearValue !== undefined &&
          selectedYear === minYearValue &&
          minMonth !== undefined
        ) {
          isDisabled = isDisabled || index < minMonth;
        }
        if (
          maxYearValue !== undefined &&
          selectedYear === maxYearValue &&
          maxMonth !== undefined
        ) {
          isDisabled = isDisabled || index > maxMonth;
        }
      }

      return {
        value: index.toString(),
        label: month,
        disabled: isDisabled,
      };
    }).filter((m) => !m.disabled);

    const years = [];
    for (let y = minYear; y <= maxYear; y++) {
      years.push({ value: y.toString(), label: y.toString() });
    }

    return (
      <div className={cn("space-y-1.5", className)}>
        {label && (
          <label
            className={cn(
              "block text-sm font-medium transition-colors duration-200",
              hasError
                ? "text-error"
                : isFocused
                  ? "text-interactive"
                  : "text-text-secondary"
            )}
          >
            {label}
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          <Select
            value={selectedMonth?.toString() || ""}
            onChange={(monthValue) => {
              const month = parseInt(monthValue);
              const year = selectedYear || currentYear;
              onDateChange(new Date(year, month, 1));
            }}
            options={availableMonths}
            placeholder="Month"
            className="flex-1 min-w-[140px]"
          />
          <Select
            value={selectedYear?.toString() || ""}
            onChange={(yearValue) => {
              const year = parseInt(yearValue);
              const month = selectedMonth !== undefined ? selectedMonth : 0;
              onDateChange(new Date(year, month, 1));
            }}
            options={years}
            placeholder="Year"
            className="flex-1 min-w-[120px]"
          />
        </div>
        {error && <p className="text-sm text-error font-medium">{error}</p>}
      </div>
    );
  }

  const pickerPopover = isOpen && (
          <>
            {isMobile ? (
              renderMobileSheet()
            ) : (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsOpen(false)}
                />
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed",
                    ...(activeTab === "date"
                      ? datePosition.top !== undefined
                        ? { top: `${datePosition.top}px` }
                        : { bottom: `${datePosition.bottom}px` }
                      : timePosition.top !== undefined
                        ? { top: `${timePosition.top}px` }
                        : { bottom: `${timePosition.bottom}px` }),
                    left: `${activeTab === "date" ? datePosition.left : timePosition.left}px`,
                    width: `${activeTab === "date" ? datePosition.width : timePosition.width}px`,
                    maxHeight: `${activeTab === "date" ? datePosition.maxHeight : timePosition.maxHeight}px`,
                  }}
                  className="z-50 rounded-2xl border border-border bg-bg-elevated shadow-elevated flex flex-col overflow-hidden"
                >
                  <div className="p-2 overflow-y-auto flex-1">
                    {activeTab === "date" ? (
                      renderCalendar()
                    ) : (
                      <div className="space-y-2">
                        {timeOptions.map((option) => {
                          const selected =
                            timeValue?.hours === option.hours &&
                            timeValue?.minutes === option.minutes &&
                            timeValue?.period === option.period;
                          return (
                            <button
                              key={`${option.hours}-${option.minutes}-${option.period}`}
                              type="button"
                              onClick={() => handleTimeSelect(option)}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors",
                                selected
                                  ? "bg-interactive text-bg-base font-semibold"
                                  : "text-text-primary hover:bg-bg-hover",
                              )}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        );

  if (editOnly) {
    return (
      <div className={cn("space-y-1.5", className)}>
        {label && (
          <label
            className={cn(
              "block text-sm font-medium transition-colors duration-200",
              hasError
                ? "text-error"
                : isFocused
                  ? "text-interactive"
                  : "text-text-secondary"
            )}
          >
            {label}
          </label>
        )}
        <div ref={containerRef} className="relative">
        <input
          ref={dateInputRef}
          type="text"
          inputMode="numeric"
          value={dateInputValue}
          onChange={handleDateInputChange}
          onKeyDown={handleDateInputKeyDown}
          onFocus={() => {
            setIsFocused(true);
            setActiveTab("date");
            setIsOpen(true);
          }}
          onBlur={handleDateInputBlur}
          placeholder={datePlaceholder || "MM/DD/YYYY"}
          className={cn(
            "w-full rounded-xl border bg-bg-elevated px-3 py-2 text-base transition-all duration-200 ease-out text-text-primary placeholder:text-text-tertiary focus:outline-none",
            hasError
              ? "border-error focus:border-error focus:ring-2 focus:ring-error/20"
              : "border-border hover:border-border-hover focus:border-interactive focus:ring-2 focus:ring-interactive/20"
          )}
        />
        {pickerPopover}
        </div>
        {error && <p className="text-sm text-error font-medium">{error}</p>}
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label
          className={cn(
            "block text-sm font-medium transition-colors duration-200",
            hasError
              ? "text-error"
              : isFocused
                ? "text-interactive"
                : "text-text-secondary",
          )}
        >
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <div
          className={cn(
            "w-full flex items-center gap-3 rounded-xl border bg-bg-elevated px-3 py-2 h-11 transition-all duration-200 ease-out",
            hasError
              ? "border-error focus-within:border-error focus-within:ring-2 focus-within:ring-error/20"
              : "border-border focus-within:border-interactive focus-within:ring-2 focus-within:ring-interactive/20",
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:border-border-hover"
          )}
        >
          <button
            type="button"
            onClick={() => {
              if (disabled) return;
              setActiveTab("date");
              setIsOpen(true);
            }}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={cn(
              "flex-1 min-w-0 text-left px-3 py-1.5 rounded-lg transition-colors text-base flex items-center justify-between",
              dateValue ? "text-text-primary" : "text-text-tertiary",
              disabled && "cursor-not-allowed"
            )}
          >
            <span className="truncate">{displayDate}</span>
            {variant === "date" && (
              <Calendar
                size={16}
                className="text-text-tertiary"
              />
            )}
          </button>

          {variant === "datetime" && (
            <div className="flex items-center gap-1 px-2">
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("time");
                    setIsOpen(true);
                  }}
                  className="text-base text-text-primary px-1"
                >
                  {displayTime}
                </button>
              ) : (
                <>
                  <input
                    ref={hoursInputRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={hours.toString().padStart(2, "0")}
                    onChange={handleHoursChange}
                    onKeyDown={handleHoursKeyDown}
                    onBlur={handleHoursBlur}
                    onFocus={(e) => {
                      setIsFocused(true);
                      setActiveTab("time");
                      setIsOpen(true);
                      e.target.select();
                    }}
                    className="w-6 text-center text-base bg-transparent text-text-primary focus:outline-none caret-text-primary"
                    aria-label="Hours"
                  />
                  <span className="text-text-primary text-base">:</span>
                  <input
                    ref={minutesInputRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={minutes.toString().padStart(2, "0")}
                    onChange={handleMinutesChange}
                    onKeyDown={handleMinutesKeyDown}
                    onBlur={handleMinutesBlur}
                    onFocus={(e) => {
                      setIsFocused(true);
                      setActiveTab("time");
                      setIsOpen(true);
                      e.target.select();
                    }}
                    className="w-6 text-center text-base bg-transparent text-text-primary focus:outline-none caret-text-primary"
                    aria-label="Minutes"
                  />
                  <input
                    ref={periodInputRef}
                    type="text"
                    maxLength={2}
                    value={period}
                    onChange={handlePeriodChange}
                    onKeyDown={handlePeriodKeyDown}
                    onBlur={handlePeriodBlur}
                    onFocus={(e) => {
                      setIsFocused(true);
                      setActiveTab("time");
                      setIsOpen(true);
                      e.target.select();
                    }}
                    className="w-7 text-center text-base bg-transparent text-text-primary focus:outline-none uppercase caret-text-primary ml-0.5"
                    aria-label="AM/PM"
                  />
                </>
              )}
            </div>
          )}
        </div>

        {pickerPopover}
      </div>
      {error && <p className="text-sm text-error font-medium">{error}</p>}
    </div>
  );
};